import React, { useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Popover from "@mui/material/Popover";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  format,
  isAfter,
  isBefore,
  subDays,
  differenceInCalendarDays,
} from "date-fns";
import { CalenderNew } from "@utils/defaultImages";

const LoaderFilter = ({
  startDate,
  endDate,
  onDateChange,
  onApplyFilter,
  loading = false,
  MAX_DAYS_RANGE = 7,
  dateErrors,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [tempError, setTempError] = useState("");

  const handleOpenPopup = (event) => {
    setAnchorEl(event.currentTarget);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setTempError("");
  };

  const handleClosePopup = () => {
    setAnchorEl(null);
    setTempError("");
  };

  const handleTempStartDateChange = (newDate) => {
    setTempStartDate(newDate);

    // Clear error if the change fixes it, or set a temporary validation error
    if (newDate && tempEndDate) {
      if (isAfter(newDate, tempEndDate)) {
        setTempError("Start date cannot be after end date");
      } else {
        setTempError("");
      }
    } else {
      setTempError("");
    }
  };

  const handleTempEndDateChange = (newDate) => {
    setTempEndDate(newDate);

    // Clear error if the change fixes it, or set a temporary validation error
    if (newDate && tempStartDate) {
      if (isBefore(newDate, tempStartDate)) {
        setTempError("End date cannot be before start date");
      } else {
        setTempError("");
      }
    } else {
      setTempError("");
    }
  };

  const handleApplyRange = () => {
    if (!tempStartDate || !tempEndDate) {
      setTempError("Both start and end dates are required");
      return;
    }

    if (isAfter(tempStartDate, tempEndDate)) {
      setTempError("Start date cannot be after end date");
      return;
    }

    // Check for max day range
    const daysDifference =
      differenceInCalendarDays(tempEndDate, tempStartDate) + 1;

    if (daysDifference > MAX_DAYS_RANGE) {
      setTempError(`You can select a maximum of ${MAX_DAYS_RANGE} days`);
      return;
    }

    onDateChange(tempStartDate, "startDate");
    onDateChange(tempEndDate, "endDate");
    onApplyFilter(tempStartDate, tempEndDate);

    handleClosePopup();
  };

  const isDisabled = loading;
  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  // Format display text
  const formatDisplayDate = (date) => {
    if (!date) return "Select Date Range";
    return `${format(date, "dd-MM-yyyy")}`;
  };

  const displayText =
    startDate && endDate
      ? `${formatDisplayDate(startDate)} to ${formatDisplayDate(endDate)}`
      : "Select Date Range";

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div
        className="min-w-[300px] bg-white rounded-xl p-2 cursor-pointer flex items-center gap-2 w-full md:w-auto justify-between shadow-md"
        onClick={handleOpenPopup}
      >
        {/* <TextField
              label="Date Range"
              value={displayText}
              onClick={handleOpenPopup}
              disabled={isDisabled}
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  cursor: "pointer",
                },
                "& .MuiOutlinedInput-input": {
                  cursor: "pointer",
                },
              }}
              size="small"
              error={
                !!dateErrors.startDate ||
                !!dateErrors.endDate ||
                !!dateErrors.global
              }
              helperText={
                dateErrors.global ||
                dateErrors.startDate ||
                dateErrors.endDate ||
                null
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {loading ? (
                      <CircularProgress thickness={6} size={20} />
                    ) : (
                      <CalendarRange size={20} />
                    )}
                  </InputAdornment>
                ),
                readOnly: true,
              }}
            /> */}
       <div className="flex gap-2 items-center">
         <h3 className="text-[#6C6A6A] text-[12px] font-[600] font-[Lato]">
          Date Range :
        </h3>
        <p className="text-[#8B8B8B] text-[12px] font-[600] font-[Lato] p-1 border border-[#E6E6E6] shadow-md">
          {displayText}
        </p>
       </div>
        <div className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] w-7 h-7 flex justify-center items-center rounded-md">
          <CalenderNew className="w-4 h-4" />
        </div>
      </div>

      {/* Popover with Date Range Selection */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopup}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPopover-paper": {
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            minWidth: "320px",
            maxWidth: "320px",
          },
        }}
      >
        <Box className="flex flex-col gap-4">
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#0575E6" }}>
            Select Date Range
          </Typography>

          {/* Start Date Picker */}
          <DatePicker
            label="Start Date"
            value={tempStartDate}
            onChange={handleTempStartDateChange}
            maxDate={tempEndDate || new Date()}
            sx={{
              width: "100%",
            }}
            format="dd-MM-yyyy"
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                },
              },
            }}
          />

          {/* End Date Picker */}
          <DatePicker
            label="End Date"
            value={tempEndDate}
            onChange={handleTempEndDateChange}
            minDate={tempStartDate}
            maxDate={new Date()}
            sx={{
              width: "100%",
            }}
            format="dd-MM-yyyy"
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                sx: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                },
              },
            }}
          />

          {/* Validation Error */}
          {tempError && (
            <Typography
              color="error"
              variant="caption"
              sx={{
                mt: "2px",
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              {tempError}
            </Typography>
          )}

          {/* Apply Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleApplyRange}
            disabled={!tempStartDate || !tempEndDate || !!tempError}
            sx={{
              backgroundColor: "#0575E6",
              "&:hover": {
                backgroundColor: "#0461c4",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
                color: "#a0a0a0",
              },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            OK
          </Button>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default LoaderFilter;
